import EmptyState from '@/components/linear/empty-state';
import IssueRow from '@/components/linear/issue-row';
import PageToolbar from '@/components/linear/page-toolbar';
import TeamIcon from '@/components/linear/team-icon';
import { openIssuePath } from '@/lib/linear-paths';
import type { Cycle } from '@/types/cycle';
import type { Issue } from '@/types/issue';
import { router } from '@inertiajs/react';

interface CycleCard extends Cycle {
  current?: boolean;
  upcoming?: boolean;
  past?: boolean;
  issueCount?: number;
  startedCount?: number;
  completedCount?: number;
  progressPercent?: number;
  issues?: Issue[];
  team?: { id: string; name: string; key: string; color: string };
}

interface Props {
  currentCycles?: CycleCard[];
  upcomingCycles?: CycleCard[];
  pastCycles?: CycleCard[];
  cycles?: CycleCard[];
}

export default function Index({ currentCycles, upcomingCycles, pastCycles, cycles = [] }: Props) {
  const current = currentCycles ?? cycles.filter((cycle) => cycle.current);
  const upcoming = upcomingCycles ?? cycles.filter((cycle) => cycle.upcoming);
  const past = pastCycles ?? cycles.filter((cycle) => cycle.past);

  return (
    <div className="ln-page">
      <PageToolbar title="Cycles" showNew={false} />
      <div className="ln-page-scroll">
        <CycleSection title="Current" cycles={current} />
        <CycleSection title="Upcoming" cycles={upcoming} />
        <CycleSection title="Past" cycles={past} />
        {current.length + upcoming.length + past.length === 0 ? (
          <EmptyState
            title="No cycles"
            description="Cycles timebox a team’s work. Start one from team settings."
          />
        ) : null}
      </div>
    </div>
  );
}

function CycleSection({ title, cycles }: { title: string; cycles: CycleCard[] }) {
  if (cycles.length === 0) return null;

  const showProgressCounts = title === 'Current';

  return (
    <section>
      <div className="ln-group-header">
        <div className="ln-group-toggle" style={{ cursor: 'default' }}>
          <span>{title}</span>
          <span className="ln-group-count">{cycles.length}</span>
        </div>
      </div>
      {cycles.map((cycle) => (
        <div key={cycle.id} style={{ borderBottom: '1px solid var(--ln-border)' }}>
          <div className="ln-row" style={{ height: 48, cursor: 'default' }}>
            {cycle.team ? <TeamIcon color={cycle.team.color} /> : null}
            <span className="ln-row-title">{cycle.name}</span>
            <span className="ln-row-meta">
              <span className="ln-inbox-sub">
                {cycle.startsOn} – {cycle.endsOn}
              </span>
              {showProgressCounts ? null : (
                <>
                  <div
                    className="ln-cycle-progress"
                    aria-label={`${cycle.progressPercent ?? 0}% complete`}
                  >
                    <div
                      className="ln-cycle-progress-fill"
                      style={{ width: `${cycle.progressPercent ?? 0}%` }}
                    />
                  </div>
                  <span className="ln-inbox-sub">
                    {cycle.completedCount ?? 0}/{cycle.issueCount ?? cycle.issues?.length ?? 0}
                  </span>
                </>
              )}
            </span>
          </div>
          {showProgressCounts ? <CycleProgressCounts cycle={cycle} /> : null}
          {cycle.issues && cycle.issues.length > 0
            ? cycle.issues.map((issue) => (
                <IssueRow
                  key={issue.id}
                  issue={issue}
                  onSelect={(next) => router.visit(openIssuePath(next))}
                />
              ))
            : null}
        </div>
      ))}
    </section>
  );
}

function CycleProgressCounts({ cycle }: { cycle: CycleCard }) {
  const scope = cycle.issueCount ?? cycle.issues?.length ?? 0;
  const started = cycle.startedCount ?? countIssues(cycle.issues, ['in_progress', 'in_review']);
  const completed = cycle.completedCount ?? countIssues(cycle.issues, ['done']);
  const startedPercent = percentOf(started, scope);
  const completedPercent = cycle.progressPercent ?? percentOf(completed, scope);

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 18,
        padding: '2px 16px 12px 40px',
      }}
      aria-label="Progress"
    >
      <ProgressCount label="Scope" value={`${scope}`} swatch="var(--ln-muted)" />
      <ProgressCount label="Started" value={`${started} · ${startedPercent}%`} swatch="#f2c94c" />
      <ProgressCount
        label="Completed"
        value={`${completed} · ${completedPercent}%`}
        swatch="var(--ln-accent)"
      />
    </div>
  );
}

function ProgressCount({ label, value, swatch }: { label: string; value: string; swatch: string }) {
  return (
    <span className="ln-inbox-sub" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span
        aria-hidden="true"
        style={{
          width: 8,
          height: 8,
          borderRadius: 2,
          background: swatch,
          flexShrink: 0,
        }}
      />
      <span>{label}</span>
      <span style={{ color: 'var(--ln-text)', fontVariantNumeric: 'tabular-nums' }}>{value}</span>
    </span>
  );
}

function countIssues(issues: Issue[] | undefined, statuses: Issue['status'][]) {
  if (issues) {
    return issues.filter((issue) => statuses.includes(issue.status)).length;
  } else {
    return 0;
  }
}

function percentOf(count: number, scope: number) {
  if (scope === 0) {
    return 0;
  } else {
    return Math.floor((count * 100) / scope);
  }
}
